import { MutationHookOptions, QueryHookOptions } from '@apollo/client';
import {
  createApiHook,
  useApiLazyQuery,
  useApiMutation,
  useApiQuery
} from '@common/hooks';
import {
  activateBusinessPartnerLines,
  addBusinessPartnerPlans,
  addClaim,
  checkBusinessUserByPerson,
  closeClaim,
  closeProposal,
  createAccountingTransfer,
  createBusinessPartner,
  createBusinessPartnerVignetteRange,
  createPersonAddress,
  createProposal,
  createProvider,
  createTaxation,
  createTaxationPlanSpecific,
  createTreatyMutation,
  createUser,
  deactivateBusinessPartnerLines,
  getAccountingTransferLovs,
  getActiveBusinessPartnerLines,
  getActiveLinePlans,
  getActivePersonsList,
  getActivePersonsWithoutBusinessPartners,
  getAdditionalInformationEnums,
  getAmendmentSubtypesQuery,
  getAssignPlansLovs,
  getBusinessPartnerLineDetails,
  getBusinessPartnerLovs,
  getBusinessPartnerTypeEnums,
  getBusinessUserLovs,
  getCountriesList,
  getCurrenciesList,
  getDefaultCommissionLovs,
  getDistinctEffectiveDates,
  getExportAmendmentsLOVs,
  getExportBusinessPartnersLovs,
  getExportClaimsLOVs,
  getExportPoliciesLOVs,
  getExportProposalsLOVs,
  getInActiveBusinessPartnerLines,
  getLatestTaxationByLine,
  getLines,
  getPersonAddressDetails,
  getPersonAddressLovs,
  getPersonDetailsById,
  getPersonDetailsForm,
  getPersonDetailsList,
  getPersonEmailDetailsInfo,
  getPlansCommissionLovs,
  getPolicies,
  getPoliciesForClaimLov,
  getPolicyPolicyPersons,
  getProposalLovs,
  getProviderLOVs,
  getReinsurerExportLovs,
  getRelatedCompaniesQuery,
  getSystemReferenceDateQuery,
  getTaxationPlanSpecificById,
  getTitlesList,
  getTreaties,
  getTreatiesExportLovs,
  getTreatyReinsurersAndListQuery,
  getUnassignedActivePlansByLine,
  getVignetteRangeDetails,
  getVignetteRangeStatusLOV,
  getZonesList,
  modifyAddress,
  modifyBusinessPartner,
  modifyBusinessPartnerLineCommission,
  modifyCancellationAmendment,
  modifyPlanSpecificTaxation,
  modifyTaxation,
  reactivateTreaty,
  searchAmendments,
  searchPolicyPersons,
  searchPoliciesForExport,
  searchReinsurers,
  searchUsersForExport,
  setPlansCommission,
  updateBusinessPartnerVignetteRange,
  updatePersonAdditionalInfo,
  updatePersonAddress,
  updatePersonInfo,
  updateTreatyReinsurers,
  updateUser,
  validateCurrencyEffectiveDate
} from '@common/services';
import {
  ActivateBusinessPartnerLinesVariables,
  AddBusinessPartnerPlansVariables,
  AddClaimResponse,
  AddClaimVariables,
  CheckBusinessUserByPersonResponse,
  CheckBusinessUserByPersonVariables,
  CloseClaimResponse,
  CloseClaimVariables,
  CloseProposalResponse,
  CloseProposalVariables,
  CreateAccoutingTransferResponse,
  CreateAccoutingTransferVariables,
  CreateAmendmentResponse,
  CreateAmendmentVariables,
  CreateBusinessPartnerResponse,
  CreateBusinessPartnerVariables,
  CreateBusinessPartnerVignetteRangeResponse,
  CreateBusinessPartnerVignetteRangeVariables,
  CreateCancellationAmendmentResponse,
  CreatePersonAddressResponse,
  CreatePersonAddressVariables,
  CreateProposalResponse,
  CreateProposalVariables,
  CreateProviderResponse,
  CreateProviderVariables,
  CreateTaxationPlanSpecificVariables,
  CreateTaxationResponse,
  CreateTaxationVariables,
  CreateTreatyResponse,
  CreateTreatyVariables,
  CreateUserResult,
  CreateUserVariables,
  GetAccountingTransferLovsResponse,
  GetActivePersonsWithoutBusinessPartnersListResponse,
  GetAmendmentsResponse,
  GetAmendmentSubtypesResponse,
  GetAmendmentsVariables,
  GetAssignPlansLovsResponse,
  GetAssignPlansLovsVariables,
  GetBusinessPartnerLineDetailsResponse,
  GetBusinessPartnerLineDetailsVariables,
  GetBusinessPartnerLovsResponse,
  GetBusinessPartnerTypeEnumListResponse,
  GetBusinessUserLovsResponse,
  GetCountriesListResponse,
  GetCurrenciesListResponse,
  GetDistinctEffectiveDatesResponse,
  GetExportAmendmentsLOVsResponse,
  GetExportBusinessPartnersLovsResponse,
  GetExportClaimsLOVsResponse,
  GetExportPoliciesLOVsResponse,
  GetExportProposalsLOVsResponse,
  GetInactiveBusinessPartnerLinesVariables,
  GetLinesResponse,
  GetLinesVariables,
  GetPersonAddressDetailsResponse,
  GetPersonAddressDetailsVariables,
  GetPersonAddressLovsResponse,
  GetPersonDetailsByIdResponse,
  GetPersonDetailsByIdVariables,
  GetPersonDetailsFormResponse,
  GetPersonDetailsResponse,
  GetPersonDetailsVariables,
  GetPersonEmailDetailsInfoResponse,
  GetPersonEmailDetailsInfoVariables,
  GetPlansCommissionLovsResponse,
  GetPlansCommissionLovsVariables,
  GetPoliciesForClaimLovResponse,
  GetPoliciesForClaimLovVariables,
  GetPolicyPolicyPersonsResponse,
  GetPolicyPolicyPersonsVariables,
  GetProposalLovsResponse,
  GetProposalLovsVariables,
  GetProviderLovsListResponse,
  GetReinsurerExportLovsResponse,
  GetReinsurersResponse,
  GetReinsurersVariables,
  GetRelatedCompaniesResponse,
  GetSetLineDefaultCommissionLovsResponse,
  GetSetLineDefaultCommissionLovsVariables,
  GetSystemReferenceDateResponse,
  GetTaxationPlanSpecificByIdResponse,
  GetTaxationPlanSpecificByIdVariables,
  GetTaxationsResponse,
  GetTaxationsVariables,
  GetTitlesListResponse,
  GetTreatiesExportLovsResponse,
  GetUnassignedActivePlansByLineResponse,
  GetUnassignedActivePlansByLineVariables,
  GetVignetteRangeDetailsResponse,
  GetVignetteRangeDetailsVariables,
  GetVignetteRangeStatusLOVResponse,
  GetZonesListResponse,
  IGetTreatyReinsurersAndListResponse,
  IGetTreatyReinsurersAndListVars,
  IUpdateTreatyReinsurersResponse,
  IUpdateTreatyReinsurersVars,
  ModifyAddressResponse,
  ModifyAddressVariables,
  ModifyAmendmentResponse,
  ModifyAmendmentVariables,
  ModifyBusinessPartnerLinesVariables,
  ModifyBusinessPartnerResponse,
  ModifyBusinessPartnerVariables,
  ModifyTaxationResponse,
  ModifyTaxationVariables,
  OrderByInput,
  PaginationInput,
  ReactivateTreatyResponse,
  ReactivateTreatyVariables,
  SearchPoliciesForExportResponse,
  SearchPoliciesForExportVariables,
  SearchPolicyPersonsResponse,
  SearchPolicyPersonsVariables,
  SearchUsersForExportResponse,
  SearchUsersForExportVariables,
  SetPlanDefaultCommissionVariables,
  UpdateBusinessPartnerVignetteRangeResponse,
  UpdateBusinessPartnerVignetteRangeVariables,
  UpdatePersonAdditionalInfoResponse,
  UpdatePersonAdditionalInfoVariables,
  UpdatePersonAddressResponse,
  UpdatePersonAddressVariables,
  UpdatePersonInfoResponse,
  UpdatePersonInfoVariables,
  UpdateUserResult,
  UpdateUserVariables,
  ValidateCurrencyEffectiveDateResponse,
  ValidateCurrencyEffectiveDateVariables
} from '@common/types';
import { getClaimDetails } from '@features/claims-management/claims/services';
import {
  getTaxationDetails,
  searchTaxations
} from '@features/configurations/taxation/services';
import { getAmendmentDetails } from '@features/production/amendment/services';
import {
  createAmendmentMutation,
  createCancellationAmendmentMutation
} from '@features/production/amendment/services/amendment.mutations';
import {
  getProposalById,
  getProposalDetails
} from '@features/production/proposal';
import {
  getBusinessPartnersList,
  GetBusinessPartnersListResponse,
  GetBusinessPartnersListVariables,
  getBusinessPartnerVignetteRangeList
} from '@features/salesforce/business-partner';
import {
  getPersonDetailsAddressList,
  getPersonDetailsInfo
} from '@features/salesforce/persons';
import { getProvidersList } from '@features/salesforce/providers';
import { getUsersList } from '@features/salesforce/users';
import { getTreatyDetailsQuery } from '@modules/reinsurance/treaties/treaty-details-page/queries';
import { getBusinessPartnerDetailsInfo } from '@modules/salesforce/business-partners/business-partner-details-page/queries';
import { getBusinessUserDetailsInfo } from '@modules/salesforce/business-users/salesforce-business-user-details-page/queries';

const useGetPersonDetailsForm = createApiHook<
  GetPersonDetailsFormResponse,
  any
>({
  query: getPersonDetailsForm,
  key: 'getPersonDetailsForm'
});

const useGetAdditionalInformationEnums = createApiHook<
  GetPersonDetailsFormResponse,
  any
>({
  query: getAdditionalInformationEnums,
  key: 'getAdditionalInformationEnums'
});

const useGetPersonAddressDetails = createApiHook<
  GetPersonAddressDetailsResponse,
  GetPersonAddressDetailsVariables
>({
  query: getPersonAddressDetails,
  key: 'getPersonAddressDetails'
});

const useGetPersonAddressLovs = createApiHook<
  GetPersonAddressLovsResponse,
  any
>({
  query: getPersonAddressLovs,
  key: 'getPersonAddressLovs'
});

const useGetActivePersonsWithoutBusinessPartners = createApiHook<
  GetActivePersonsWithoutBusinessPartnersListResponse,
  any
>({
  query: getActivePersonsWithoutBusinessPartners,
  key: 'getActivePersonsWithoutBusinessPartners'
});

const useGetBusinessPartnerTypeEnums = createApiHook<
  GetBusinessPartnerTypeEnumListResponse,
  any
>({
  query: getBusinessPartnerTypeEnums,
  key: 'getBusinessPartnerTypeEnums'
});

const useGetCountries = createApiHook<GetCountriesListResponse, any>({
  query: getCountriesList,
  key: 'getCountriesList'
});

const useGetCurrencies = createApiHook<GetCurrenciesListResponse, any>({
  query: getCurrenciesList,
  key: 'getCurrenciesList'
});

const useGetTitles = createApiHook<GetTitlesListResponse, any>({
  query: getTitlesList,
  key: 'getTitlesList'
});

const useGetZones = createApiHook<GetZonesListResponse, any>({
  query: getZonesList,
  key: 'getZonesList'
});

const useGetRelatedCompanies = createApiHook<GetRelatedCompaniesResponse, any>({
  query: getRelatedCompaniesQuery,
  key: 'getRelatedCompanies'
});

const useGetPersonDetails = createApiHook<
  GetPersonDetailsResponse,
  GetPersonDetailsVariables
>({
  query: getPersonDetailsList,
  key: 'getPersonDetailsList'
});

const useGetVignetteRangeStatusLOV = createApiHook<
  GetVignetteRangeStatusLOVResponse,
  any
>({
  query: getVignetteRangeStatusLOV,
  key: 'getVignetteRangeStatusLOV'
});

const useGetVignetteRangeDetails = createApiHook<
  GetVignetteRangeDetailsResponse,
  GetVignetteRangeDetailsVariables
>({
  query: getVignetteRangeDetails,
  key: 'getVignetteRangeDetails'
});

const useGetProviderLovs = createApiHook<GetProviderLovsListResponse, any>({
  query: getProviderLOVs,
  key: 'getProviderLOVs'
});

const useGetActivePersons = createApiHook<GetBusinessPartnerLovsResponse, any>({
  query: getActivePersonsList,
  key: 'getActivePersonsList'
});

const useGetBusinessUserLovs = createApiHook<GetBusinessUserLovsResponse, any>({
  query: getBusinessUserLovs,
  key: 'getBusinessUserLovs'
});

const useGetPersonDetailsById = createApiHook<
  GetPersonDetailsByIdResponse,
  GetPersonDetailsByIdVariables
>({
  query: getPersonDetailsById,
  key: 'getPersonDetailsById'
});

const useCheckUserByPerson = createApiHook<
  CheckBusinessUserByPersonResponse,
  CheckBusinessUserByPersonVariables
>({
  query: checkBusinessUserByPerson,
  key: 'checkBusinessUserByPerson'
});

const useGetPersonEmailDetailsInfo = createApiHook<
  GetPersonEmailDetailsInfoResponse,
  GetPersonEmailDetailsInfoVariables
>({
  query: getPersonEmailDetailsInfo,
  key: 'getPersonEmailDetailsInfo'
});

const useGetExportPoliciesLOVs = createApiHook<
  GetExportPoliciesLOVsResponse,
  any
>({
  query: getExportPoliciesLOVs,
  key: 'getExportPoliciesLOVs'
});

const useSearchPoliciesForExport = createApiHook<
  SearchPoliciesForExportResponse,
  SearchPoliciesForExportVariables
>({
  query: searchPoliciesForExport,
  key: 'searchPoliciesForExport'
});

const useSearchPolicyPersons = createApiHook<
  SearchPolicyPersonsResponse,
  SearchPolicyPersonsVariables
>({
  query: searchPolicyPersons,
  key: 'searchPolicyPersons',
  successMsg: 'Policies successfully exported',
  buttonLoader: true
});

const useGetExportAmendmentsLOVs = createApiHook<
  GetExportAmendmentsLOVsResponse,
  any
>({
  query: getExportAmendmentsLOVs,
  key: 'getExportAmendmentsLOVs'
});

const useGetAmendments = createApiHook<
  GetAmendmentsResponse,
  GetAmendmentsVariables
>({
  query: searchAmendments,
  key: 'searchAmendments',
  buttonLoader: true,
  successMsg: 'Amendments successfully exported'
});

const useModifyAmendment = (
  options?: MutationHookOptions<
    ModifyAmendmentResponse,
    ModifyAmendmentVariables
  >
) =>
  useApiMutation<ModifyAmendmentResponse, ModifyAmendmentVariables>(
    modifyCancellationAmendment,
    'modifyAmendment',
    {
      withLoader: false,
      successMsg: 'Amendment modified successfully'
    },
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      refetchQueries: [getAmendmentDetails],
      awaitRefetchQueries: true
    }
  );

const useUpatePersonInfo = (personId: string) =>
  useApiMutation<UpdatePersonInfoResponse, UpdatePersonInfoVariables>(
    updatePersonInfo,
    'updatePersonInfo',
    { successMsg: 'Person updated successfully!' },
    {
      refetchQueries: [
        { query: getPersonDetailsInfo, variables: { id: personId } }
      ],
      awaitRefetchQueries: true
    }
  );

const useUpatePersonAdditionalInfo = (personId: string) =>
  useApiMutation<
    UpdatePersonAdditionalInfoResponse,
    UpdatePersonAdditionalInfoVariables
  >(
    updatePersonAdditionalInfo,
    'updatePersonInfo',
    { successMsg: 'Person Additional updated successfully!' },
    {
      refetchQueries: [
        { query: getPersonDetailsInfo, variables: { id: personId } }
      ],
      awaitRefetchQueries: true
    }
  );

const useCreatePersonAddress = (
  personId: string,
  filter: {
    pagination: PaginationInput;
    OrderBy: OrderByInput;
  }
) =>
  useApiMutation<CreatePersonAddressResponse, CreatePersonAddressVariables>(
    createPersonAddress,
    'createPersonAddress',
    { successMsg: 'Person address created successfully!' },
    {
      refetchQueries: [
        {
          query: getPersonDetailsAddressList,
          variables: {
            personId,
            pagination: filter.pagination,
            OrderBy: filter.OrderBy
          }
        }
      ],
      awaitRefetchQueries: true
    }
  );

const useUpdatePersonAddress = (
  personId: string,
  filter: {
    pagination: PaginationInput;
    OrderBy: OrderByInput;
  }
) =>
  useApiMutation<UpdatePersonAddressResponse, UpdatePersonAddressVariables>(
    updatePersonAddress,
    'updatePersonAddress',
    { successMsg: 'Person address updated successfully!' },
    {
      refetchQueries: [
        {
          query: getPersonDetailsAddressList,
          variables: {
            personId,
            pagination: filter.pagination,
            OrderBy: filter.OrderBy
          }
        }
      ],
      awaitRefetchQueries: true
    }
  );

const useCreateBusinessPartner = () =>
  useApiMutation<CreateBusinessPartnerResponse, CreateBusinessPartnerVariables>(
    createBusinessPartner,
    'createBusinessPartner',
    { successMsg: 'Business partner created successfully!' },
    {
      refetchQueries: [
        {
          query: getBusinessPartnersList,
          variables: {}
        }
      ],
      awaitRefetchQueries: true
    }
  );

const useCreateVignetteRange = (businessPartnerId: string) =>
  useApiMutation<
    CreateBusinessPartnerVignetteRangeResponse,
    CreateBusinessPartnerVignetteRangeVariables
  >(
    createBusinessPartnerVignetteRange,
    'createBusinessPartnerVignetteRange',
    { successMsg: 'Vignette Range assigned successfully!' },
    {
      refetchQueries: [
        {
          query: getBusinessPartnerVignetteRangeList,
          variables: {
            businessPartnerId,
            OrderBy: {
              descending: false,
              attribute: 'VignetteYear'
            },
            pagination: {
              pageSize: 10,
              pageNumber: 1
            }
          }
        }
      ],
      awaitRefetchQueries: true
    }
  );

const useUpdateVignetteRange = (
  options?: MutationHookOptions<
    UpdateBusinessPartnerVignetteRangeResponse,
    UpdateBusinessPartnerVignetteRangeVariables
  >
) =>
  useApiMutation<
    UpdateBusinessPartnerVignetteRangeResponse,
    UpdateBusinessPartnerVignetteRangeVariables
  >(
    updateBusinessPartnerVignetteRange,
    'updateBusinessPartnerVignetteRange',
    { successMsg: 'Vignette Range updated successfully!' },
    {
      refetchQueries: [
        {
          query: getBusinessPartnerDetailsInfo,
          variables: {
            id: options?.variables?.modifyBusinessPartnerVignetteInput
              ?.businessPartnerID
          }
        }
      ],
      awaitRefetchQueries: true
    }
  );

const useModifyBusinessPartner = (businessPartnerId: string) =>
  useApiMutation<ModifyBusinessPartnerResponse, ModifyBusinessPartnerVariables>(
    modifyBusinessPartner,
    'modifyBusinessPartner',
    { successMsg: 'Business Partner updated successfully!' },
    {
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      refetchQueries: [getBusinessPartnerDetailsInfo],
      awaitRefetchQueries: true
    }
  );

const useUpdateUser = (userId?: string) =>
  useApiMutation<UpdateUserResult, UpdateUserVariables>(
    updateUser,
    'updateUser',
    { successMsg: 'User was modified successfully!', withLoader: false },
    {
      refetchQueries: [
        {
          query: getPersonDetailsById,
          variables: { id: userId }
        }
      ],
      awaitRefetchQueries: true
    }
  );

const useCreateUser = () =>
  useApiMutation<CreateUserResult, CreateUserVariables>(
    createUser,
    'createUser',
    { successMsg: 'User created successfully!', withLoader: false },
    {
      refetchQueries: [
        {
          query: getUsersList
        }
      ],
      awaitRefetchQueries: true
    }
  );

const useModifyAddress = () =>
  useApiMutation<ModifyAddressResponse, ModifyAddressVariables>(
    modifyAddress,
    'modifyAddress',
    { successMsg: 'Address updated successfully!' },
    {
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      refetchQueries: [
        getBusinessPartnerDetailsInfo,
        getBusinessUserDetailsInfo
      ],
      awaitRefetchQueries: true
    }
  );

const useCreateProvider = () =>
  useApiMutation<CreateProviderResponse, CreateProviderVariables>(
    createProvider,
    'createProvider',
    {
      successMsg: 'Provider created successfully!',
      withLoader: false,
      buttonLoader: true
    },
    {
      refetchQueries: [
        {
          query: getProvidersList,
          variables: {
            pagination: {
              pageSize: 10,
              pageNumber: 1
            },
            orderBy: {
              attribute: 'createdOn',
              descending: false
            }
          }
        }
      ],
      awaitRefetchQueries: true
    }
  );

const useCreateAccoutingTransfer = (companyId: string) =>
  useApiMutation<
    CreateAccoutingTransferResponse,
    CreateAccoutingTransferVariables
  >(createAccountingTransfer, 'createAccountingTransfer', {
    successMsg: 'Transfer was created successfully!',
    withLoader: false,
    buttonLoader: true
  });

const useGetAccountingTransferLovs = createApiHook<
  GetAccountingTransferLovsResponse,
  any
>({
  query: getAccountingTransferLovs,
  key: 'getAccountingTransferLovs'
});

const useGetAmendmentSubtypes = createApiHook<
  GetAmendmentSubtypesResponse,
  any
>({
  query: getAmendmentSubtypesQuery,
  key: 'getAmendmentSubtypes'
});

const useCreateAmendment = () =>
  useApiMutation<CreateAmendmentResponse, CreateAmendmentVariables>(
    createAmendmentMutation,
    'createAmendment',
    {
      successMsg: 'Amendment created successfully!',
      withLoader: false,
      buttonLoader: true
    },
    {
      awaitRefetchQueries: true
    }
  );

const useCreateCancellationAmendment = () =>
  useApiMutation<CreateCancellationAmendmentResponse, CreateAmendmentVariables>(
    createCancellationAmendmentMutation,
    'createCancellationAmendment',
    {
      successMsg: 'Cancellation amendment created successfully!',
      withLoader: false,
      buttonLoader: true
    }
  );

const useGetSystemReferenceDate = createApiHook<
  GetSystemReferenceDateResponse,
  any
>({
  query: getSystemReferenceDateQuery,
  key: 'getSystemReferenceDateQuery'
});

const useGetProposalLovs = createApiHook<
  GetProposalLovsResponse,
  GetProposalLovsVariables
>({
  query: getProposalLovs,
  key: 'getProposalLovs'
});

const useCreateProposal = () =>
  useApiMutation<CreateProposalResponse, CreateProposalVariables>(
    createProposal,
    'createProposal',
    {
      successMsg: 'Proposal created successfully!',
      withLoader: false,
      buttonLoader: true
    }
  );

const useGetExportProposalsLOVs = createApiHook<
  GetExportProposalsLOVsResponse,
  any
>({
  query: getExportProposalsLOVs,
  key: 'getExportProposalsLOVs'
});

const useSearchUsersForExport = createApiHook<
  SearchUsersForExportResponse,
  SearchUsersForExportVariables
>({
  query: searchUsersForExport,
  key: 'searchUsersForExport'
});

const useCloseProposal = (
  options?: MutationHookOptions<CloseProposalResponse, CloseProposalVariables>
) =>
  useApiMutation<CloseProposalResponse, CloseProposalVariables>(
    closeProposal,
    'closeProposal',
    {
      successMsg: 'Proposal closed successfully!',
      withLoader: false,
      buttonLoader: true,
      ...options
    },
    {
      refetchQueries: [getProposalDetails, getProposalById],
      awaitRefetchQueries: true
    }
  );

const useSearchPoliciesForClaimLov = createApiHook<
  GetPoliciesForClaimLovResponse,
  GetPoliciesForClaimLovVariables
>({
  query: getPoliciesForClaimLov,
  key: 'getPoliciesForClaimLov'
});

const useGetPolicyPolicyPersons = createApiHook<
  GetPolicyPolicyPersonsResponse,
  GetPolicyPolicyPersonsVariables
>({
  query: getPolicyPolicyPersons,
  key: 'getPolicyPolicyPersons'
});

const useAddClaim = (
  options?: MutationHookOptions<AddClaimResponse, AddClaimVariables>
) =>
  useApiMutation<AddClaimResponse, AddClaimVariables>(addClaim, 'addClaim', {
    successMsg: 'Claim created successfully!',
    withLoader: false,
    buttonLoader: true,
    ...options
  });

const useCloseClaim = (
  options?: MutationHookOptions<CloseClaimResponse, CloseClaimVariables>
) =>
  useApiMutation<CloseClaimResponse, CloseClaimVariables>(
    closeClaim,
    'closeClaim',
    {
      successMsg: 'Claim closed successfully!',
      withLoader: false,
      ...options
    },
    { refetchQueries: [getClaimDetails], awaitRefetchQueries: true }
  );

const useGetExportClaimsLOVs = createApiHook<GetExportClaimsLOVsResponse, any>({
  query: getExportClaimsLOVs,
  key: 'getExportClaimsLOVs'
});

const useGetReinsurerExportLovs = createApiHook<
  GetReinsurerExportLovsResponse,
  any
>({
  query: getReinsurerExportLovs,
  key: 'getReinsurerExportLovs'
});

const useGetTreatiesExportLovs = createApiHook<
  GetTreatiesExportLovsResponse,
  any
>({
  query: getTreatiesExportLovs,
  key: 'getTreatiesExportLovs'
});

const useGetReinsurers = createApiHook<
  GetReinsurersResponse,
  GetReinsurersVariables
>({
  query: searchReinsurers,
  key: 'searchReinsurers',
  successMsg: 'Reinsurers successfully exported',
  buttonLoader: true,
  withLoader: false
});

const useCreateTreaty = (
  options?: MutationHookOptions<CreateTreatyResponse, CreateTreatyVariables>
) =>
  useApiMutation<CreateTreatyResponse, CreateTreatyVariables>(
    createTreatyMutation,
    'createTreaty',
    {
      ...options,
      withLoader: false,
      successMsg: 'Treaty was created successfully!'
    }
  );

const useGetTreatyReinsurersAndList = createApiHook<
  IGetTreatyReinsurersAndListResponse,
  IGetTreatyReinsurersAndListVars
>({
  query: getTreatyReinsurersAndListQuery,
  key: 'getTreatyReinsurersAndList'
});

// Hook for update treaty reinsurers mutation
const useUpdateTreatyReinsurers = (
  options?: MutationHookOptions<
    IUpdateTreatyReinsurersResponse,
    IUpdateTreatyReinsurersVars
  >
) =>
  useApiMutation<IUpdateTreatyReinsurersResponse, IUpdateTreatyReinsurersVars>(
    updateTreatyReinsurers,
    'updateTreatyReinsurers',
    {
      ...options,
      withLoader: false,
      successMsg: 'Reinsurers were updated successfully!'
    },
    { refetchQueries: [getTreatyDetailsQuery], awaitRefetchQueries: true }
  );

// Policy Distribution Hooks
const useGetPolicyDistributionLovs = (options?: QueryHookOptions<any>) => {
  const policiesResult = useApiQuery<any>(
    getPolicies,
    'getPolicies',
    {
      fetchPolicy: 'cache-and-network',
      ...(options ?? {})
    },
    { withLoader: false }
  );

  const treatiesResult = useApiQuery<any>(
    getTreaties,
    'getTreaties',
    {
      fetchPolicy: 'cache-and-network',
      ...(options ?? {})
    },
    { withLoader: false }
  );

  return {
    data: policiesResult.data,
    data2: treatiesResult.data
  };
};
// Reactivate Treaty Hook
const useReactivateTreaty = (
  options?: MutationHookOptions<
    ReactivateTreatyResponse,
    ReactivateTreatyVariables
  >
) =>
  useApiMutation<ReactivateTreatyResponse, ReactivateTreatyVariables>(
    reactivateTreaty,
    'reactivateTreaty',
    {
      successMsg: 'Treaty was reactivated successfully!',
      withLoader: false,
      ...options
    },
    { ...options }
  );

const useValidateCurrencyEffectiveDate = createApiHook<
  ValidateCurrencyEffectiveDateResponse,
  ValidateCurrencyEffectiveDateVariables
>({
  query: validateCurrencyEffectiveDate,
  key: 'validateCurrencyEffectiveDate',
  successMsg: 'Effective date validated successfully!',
  buttonLoader: true
});

const useGetDistinctEffectiveDates = createApiHook<
  GetDistinctEffectiveDatesResponse,
  any
>({
  query: getDistinctEffectiveDates,
  key: 'getDistinctEffectiveDates'
});

const useCreateTaxation = () =>
  useApiMutation<CreateTaxationResponse, CreateTaxationVariables>(
    createTaxation,
    'createTaxation',
    { withLoader: true, successMsg: 'Taxation was added successfully' },
    {
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      refetchQueries: [searchTaxations]
    }
  );

const useGetLines = createApiHook<GetLinesResponse, GetLinesVariables>({
  query: getLines,
  key: 'getLines'
});

const useGetLatestTaxationByLine = createApiHook<any, any>({
  query: getLatestTaxationByLine,
  key: 'getLatestTaxationByLine'
});

const useGetBusinessPartnersExportLovs = createApiHook<
  GetExportBusinessPartnersLovsResponse,
  any
>({
  query: getExportBusinessPartnersLovs,
  key: 'getExportBusinessPartnersLovs'
});

const useGetBusinessPartnerLovs = createApiHook<
  GetBusinessPartnerLovsResponse,
  any
>({
  query: getBusinessPartnerLovs,
  key: 'getBusinessPartnerLovs'
});

const useGetTaxations = createApiHook<
  GetTaxationsResponse,
  GetTaxationsVariables
>({
  query: searchTaxations,
  key: 'searchTaxations',
  successMsg: 'Taxation successfully exported',
  buttonLoader: true
});

const useGetBusinessPartners = createApiHook<
  GetBusinessPartnersListResponse,
  GetBusinessPartnersListVariables
>({
  query: getBusinessPartnersList,
  key: 'getBusinessPartnersList',
  successMsg: 'Business partners successfully exported',
  buttonLoader: true
});

const useModifyTaxation = () =>
  useApiMutation<ModifyTaxationResponse, ModifyTaxationVariables>(
    modifyTaxation,
    'modifyTaxation',
    { withLoader: true, successMsg: 'Taxation was updated successfully' },
    {
      fetchPolicy: 'no-cache',
      refetchQueries: [getTaxationDetails],
      notifyOnNetworkStatusChange: true
    }
  );

const useCreateTaxationPlanSpecific = () =>
  useApiMutation<any, CreateTaxationPlanSpecificVariables>(
    createTaxationPlanSpecific,
    'createTaxationPlanSpecific',
    {
      withLoader: true,
      successMsg: 'Plan Specific Taxation was added successfully'
    }
  );

const useModifyTaxationPlanSpecific = () =>
  useApiMutation<any, CreateTaxationPlanSpecificVariables>(
    modifyPlanSpecificTaxation,
    'modifyPlanSpecificTaxation',
    {
      withLoader: true,
      successMsg: 'Plan Specific Taxation was updated successfully'
    }
  );

const useGetActiveLinePlans = createApiHook<any, any>({
  query: getActiveLinePlans,
  key: 'getActiveLinePlans'
});

const useGetTaxationPlanSpecificById = createApiHook<
  GetTaxationPlanSpecificByIdResponse,
  GetTaxationPlanSpecificByIdVariables
>({
  query: getTaxationPlanSpecificById,
  key: 'getTaxationPlanSpecificById'
});

const useGetInactiveBusinessPartnerLines = createApiHook<
  any,
  GetInactiveBusinessPartnerLinesVariables
>({
  query: getInActiveBusinessPartnerLines,
  key: 'getInActiveBusinessPartnerLines'
});

const useGetActiveBusinessPartnerLines = createApiHook<
  any,
  GetInactiveBusinessPartnerLinesVariables
>({
  query: getActiveBusinessPartnerLines,
  key: 'getActiveBusinessPartnerLines'
});

const useGetUnassignedActivePlansByLine = createApiHook<
  GetUnassignedActivePlansByLineResponse,
  GetUnassignedActivePlansByLineVariables
>({
  query: getUnassignedActivePlansByLine,
  key: 'getUnassignedActivePlansByLine'
});

const useActivateBusinessPartnerLines = () =>
  useApiMutation<any, ActivateBusinessPartnerLinesVariables>(
    activateBusinessPartnerLines,
    'activateBusinessPartnerLines',
    {
      withLoader: true,
      successMsg: 'Lines & Plans were activated successfully!'
    }
  );

const useDeactivateBusinessPartnerLines = () =>
  useApiMutation<any, ActivateBusinessPartnerLinesVariables>(
    deactivateBusinessPartnerLines,
    'deactivateBusinessPartnerLines',
    {
      withLoader: true,
      successMsg: 'Lines & Plans were deactivated successfully!'
    },
    {}
  );

const useGetDefaultCommissionLovs = createApiHook<
  GetSetLineDefaultCommissionLovsResponse,
  GetSetLineDefaultCommissionLovsVariables
>({
  query: getDefaultCommissionLovs,
  key: 'getDefaultCommissionLovs'
});

const useModifyBusinessPartnerLineCommission = () =>
  useApiMutation<any, ModifyBusinessPartnerLinesVariables>(
    modifyBusinessPartnerLineCommission,
    'modifyBusinessPartnerLineCommission',
    {
      withLoader: true,
      successMsg: 'Line Default Commission updated successfully!'
    }
  );

const useGetAssignPlansLovs = createApiHook<
  GetAssignPlansLovsResponse,
  GetAssignPlansLovsVariables
>({
  query: getAssignPlansLovs,
  key: 'getAssignPlansLovs'
});

const useGetBusinessPartnerLineDetails = createApiHook<
  GetBusinessPartnerLineDetailsResponse,
  GetBusinessPartnerLineDetailsVariables
>({
  query: getBusinessPartnerLineDetails,
  key: 'getBusinessPartnerLineDetails'
});

const useAddBusinessPartnerPlans = () =>
  useApiMutation<any, AddBusinessPartnerPlansVariables>(
    addBusinessPartnerPlans,
    'addBusinessPartnerPlans',
    {
      withLoader: true,
      successMsg: 'Plan(s) were added successfully!'
    }
  );

const useGetPlansCommissionLovs = createApiHook<
  GetPlansCommissionLovsResponse,
  GetPlansCommissionLovsVariables
>({
  query: getPlansCommissionLovs,
  key: 'getPlansCommissionLovs'
});

const useSetPlansCommission = () =>
  useApiMutation<any, SetPlanDefaultCommissionVariables>(
    setPlansCommission,
    'setPlansCommission',
    { successMsg: ' Plans Commission was updated successfully!' }
  );

export {
  useActivateBusinessPartnerLines,
  useAddBusinessPartnerPlans,
  useAddClaim,
  useCheckUserByPerson,
  useCloseClaim,
  useCloseProposal,
  useCreateAccoutingTransfer,
  useCreateAmendment,
  useCreateBusinessPartner,
  useCreateCancellationAmendment,
  useCreatePersonAddress,
  useCreateProposal,
  useCreateProvider,
  useCreateTaxation,
  useCreateTaxationPlanSpecific,
  useCreateTreaty,
  useCreateUser,
  useCreateVignetteRange,
  useDeactivateBusinessPartnerLines,
  useGetAccountingTransferLovs,
  useGetActiveBusinessPartnerLines,
  useGetActiveLinePlans,
  useGetActivePersons,
  useGetAdditionalInformationEnums,
  useGetAmendments,
  useGetAmendmentSubtypes,
  useGetAssignPlansLovs,
  useGetBusinessPartnerLineDetails,
  useGetBusinessPartnerLovs,
  useGetBusinessPartners,
  useGetBusinessPartnersExportLovs,
  useGetBusinessUserLovs,
  useGetDistinctEffectiveDates,
  useGetExportAmendmentsLOVs,
  useGetExportClaimsLOVs,
  useGetExportPoliciesLOVs,
  useGetExportProposalsLOVs,
  useGetInactiveBusinessPartnerLines,
  useGetLatestTaxationByLine,
  useGetLines,
  useGetPersonAddressDetails,
  useGetPersonAddressLovs,
  useGetPersonDetailsById,
  useGetPersonDetailsForm,
  useGetPersonEmailDetailsInfo,
  useGetPlansCommissionLovs,
  useGetPolicyDistributionLovs,
  useGetPolicyPolicyPersons,
  useGetProposalLovs,
  useGetProviderLovs,
  useGetReinsurerExportLovs,
  useGetReinsurers,
  useGetRelatedCompanies,
  useGetSystemReferenceDate,
  useGetTaxationPlanSpecificById,
  useGetTaxations,
  useGetTreatiesExportLovs,
  useGetTreatyReinsurersAndList,
  useGetUnassignedActivePlansByLine,
  useGetVignetteRangeDetails,
  useGetVignetteRangeStatusLOV,
  useModifyAmendment,
  useModifyBusinessPartner,
  useModifyBusinessPartnerLineCommission,
  useModifyTaxation,
  useModifyTaxationPlanSpecific,
  useReactivateTreaty,
  useSearchPoliciesForClaimLov,
  useSearchPolicyPersons,
  useSearchPoliciesForExport,
  useSearchUsersForExport,
  useSetPlansCommission,
  useUpatePersonAdditionalInfo,
  useUpatePersonInfo,
  useUpdatePersonAddress,
  useUpdateTreatyReinsurers,
  useUpdateUser,
  useUpdateVignetteRange,
  useValidateCurrencyEffectiveDate,
  useGetCurrencies,
  useGetTitles,
  useGetZones,
  useGetCountries,
  useGetBusinessPartnerTypeEnums,
  useGetPersonDetails,
  useGetActivePersonsWithoutBusinessPartners,
  useGetDefaultCommissionLovs,
  useModifyAddress
};
